import { useState, useEffect, useRef } from 'react'

/**
 * Hook to detect when an element enters the viewport
 *
 * 依赖数组里只放基础类型（threshold / rootMargin），【不要】放整个 options 对象：
 * 调用方通常传行内对象 `useInView({ threshold: 0.3 })`，每次渲染都是新引用，
 * 把它整个塞进依赖会让 observer 每渲染一次就重建一次（eslint 的 exhaustive-deps
 * 会建议你那么做，但那是错的）。见 src/test/hooks-deps.test.jsx。
 *
 * @param {Object} options - IntersectionObserver options（threshold / rootMargin）
 * @returns {[React.RefObject, boolean]} - ref to attach and visibility state
 */
export function useInView({ threshold = 0.1, rootMargin = '0px' } = {}) {
  const [isInView, setIsInView] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true)
          observer.unobserve(element) // Only trigger once
        }
      },
      { threshold, rootMargin }
    )

    observer.observe(element)
    return () => observer.disconnect()
  }, [threshold, rootMargin])

  return [ref, isInView]
}

/**
 * Hook for typewriter text effect
 * @param {string} text - Text to type
 * @param {boolean} enabled - Whether to start typing
 * @param {number} speed - Typing speed in ms per character
 * @returns {string} - Currently displayed text
 */
export function useTypewriter(text, enabled = false, speed = 50) {
  const [displayText, setDisplayText] = useState('')

  useEffect(() => {
    if (!enabled) {
      setDisplayText('')
      return
    }

    let index = 0
    setDisplayText('')

    const interval = setInterval(() => {
      if (index < text.length) {
        setDisplayText(text.slice(0, index + 1))
        index++
      } else {
        clearInterval(interval)
      }
    }, speed)

    return () => clearInterval(interval)
  }, [text, enabled, speed])

  return displayText
}

/**
 * Hook for staggered children animation
 * @param {number} itemCount - Number of items
 * @param {boolean} enabled - Whether animation has started
 * @param {number} staggerDelay - Delay between each item in ms
 * @returns {boolean[]} - Array of visibility states
 */
export function useStaggerAnimation(itemCount, enabled = false, staggerDelay = 150) {
  const [visibleItems, setVisibleItems] = useState(new Array(itemCount).fill(false))

  useEffect(() => {
    if (!enabled) return

    const timers = []
    for (let i = 0; i < itemCount; i++) {
      const timer = setTimeout(() => {
        setVisibleItems(prev => {
          const next = [...prev]
          next[i] = true
          return next
        })
      }, i * staggerDelay)
      timers.push(timer)
    }

    return () => timers.forEach(clearTimeout)
  }, [enabled, itemCount, staggerDelay])

  return visibleItems
}
